import { Button, Divider, Form, Input } from 'antd';
import Steps from 'rc-steps';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StepWizard from 'react-step-wizard';
import parse from 'html-react-parser';
import { StepsButton } from './BtnSave';
import init from './init.json';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import ModalDelete from '../../../components/Shared/modal/delete';
import { useDpiaContext } from '../dpia.context';

export const FormObserve = ({ name: fieldName, form }) => {
    const { t } = useTranslation(['translation', 'dpia']);

    return (
        <Form.List name={fieldName}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <div
                            key={key}
                            className="bg-[#F4F6FC] p-4 mb-4 rounded-t-lg w-full"
                        >
                            <FormItemObserve
                                form={form}
                                fieldName={fieldName}
                                name={name}
                                restField={restField}
                                remove={remove}
                            />
                        </div>
                    ))}
                    <Form.Item>
                        <Button
                            className="text-[#1890FF]"
                            type="link"
                            onClick={() => add()}
                            icon={<PlusOutlined />}
                        >
                            <span className="underline">
                                {t('Add Topic', {
                                    ns: 'dpia',
                                })}
                            </span>
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form.List>
    );
};

const FormItemObserve = ({ name, restField, remove, fieldName, form }) => {
    const [openDelete, setOpenDelete] = useState(false);
    const { t } = useTranslation(['translation', 'dpia']);
    const values = Form.useWatch(fieldName, form);
    return (
        <>
            <Button
                className="text-[#1890FF] absolute right-0 z-50"
                type="link"
                onClick={() => {
                    if (
                        values[name]?.topicName ||
                        values[name]?.topicDescription
                    )
                        setOpenDelete(true);
                    else remove(name);
                }}
                icon={<MinusCircleOutlined />}
            >
                {t('Delete Topic', { ns: 'dpia' })}
            </Button>
            <Form.Item
                {...restField}
                name={[name, 'topicName']}
                label={
                    <span className="text-base">
                        {t('Topic', { ns: 'dpia' })}
                    </span>
                }
                rules={[
                    {
                        required: true,
                        message: t('errorText.requiredField'),
                    },
                ]}
                className="w-full"
            >
                <Input
                    className="!bg-white"
                    size="large"
                    rows={4}
                    showCount
                    maxLength={150}
                    style={{ width: '100%' }}
                    placeholder={t('Topic', {
                        ns: 'dpia',
                    })}
                />
            </Form.Item>
            <Form.Item
                {...restField}
                name={[name, 'topicDescription']}
                label={
                    <span className="text-base">
                        {t('Description', { ns: 'dpia' })}
                    </span>
                }
                rules={[
                    {
                        required: true,
                        message: t('errorText.requiredField'),
                    },
                ]}
                className="w-full"
            >
                <Input.TextArea
                    className="!bg-white"
                    rows={4}
                    showCount
                    maxLength={1000}
                    style={{ width: '100%' }}
                    placeholder={t('Details', {
                        ns: 'dpia',
                    })}
                />
            </Form.Item>
            <ModalDelete
                text={t('Do you want to remove this topic', {
                    ns: 'dpia',
                })}
                open={openDelete}
                handelOk={() => {
                    setOpenDelete(false);
                    remove(name);
                }}
                handelClose={() => setOpenDelete(false)}
            />
        </>
    );
};

export default function Step2(props) {
    const { t } = useTranslation(['translation', 'dpia']);
    const [current, setCurrent] = useState(0);
    const [state, updateState] = useState();
    const { setStep2, step2State, setStepNo } = useDpiaContext();

    useEffect(() => {
        if (step2State.value >= 0) {
            setCurrent(step2State.value);
            state?.SW?.goToStep(step2State.value + 1);
        }
    }, [step2State]);

    

    useEffect(() => {
        setStepNo(2)
    }, []); 

    const onChange = (value) => {
        setCurrent(value);
        state?.SW?.goToStep(value + 1);
    };

    const setInstance = (SW) => {
        updateState({
            ...state,
            SW,
        });
    };

    const onValuesChange = (info, value) => {
        const checkValue = (item) =>
            item?.topicName === '' ||
            item?.topicName === null ||
            item?.topicDescription === '' ||
            item?.topicDescription === null;

        const hasNature = value?.natureDescription?.some(checkValue);
        const hasScope = value?.scopeDescription?.some(checkValue);
        const hasContext = value?.contextDescription?.some(checkValue);
        const hasPurpose = value?.purposeDescription?.some(checkValue);

        if (hasNature || hasScope || hasContext || hasPurpose) {
            setStep2(false);
        } else {
            setStep2(true);
        }
    };

    const onNextStep = () => {
        props.form
            .validateFields()
            .then(() => {
                setStep2(true);
            })
            .catch(() => {
                setStep2(false);
            });
        props.nextStep();
    };

    return (
        <div hidden={!props?.isActive}>
            <div className="w-full flex justify-end">
                <Steps
                    className="bg-[#F5F6FF] rounded-md inline-step"
                    type="inline"
                    current={current}
                    onChange={onChange}
                    items={[
                        {
                            status: current === 0 ? 'process' : 'wait',
                            title: '2.1',
                        },
                        {
                            status: current === 1 ? 'process' : 'wait',
                            title: '2.2',
                        },
                        {
                            status: current === 2 ? 'process' : 'wait',
                            title: '2.3',
                        },
                        {
                            status: current === 3 ? 'process' : 'wait',
                            title: '2.4',
                        },
                    ]}
                />
            </div>
            <div className="text-2xl font-semibold text-purple-PHX">
                {t('Step', { ns: 'dpia' })} 2 : Description
            </div>
            <div className="text-[#65707A]">
                {t(
                    'Explain the details of the personal data processing process, which must include at least the nature, scope, context, and purpose of the processing',
                    { ns: 'dpia' }
                )}
            </div>
            <Divider />
            <Form
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                }}
                onValuesChange={onValuesChange}
                initialValues={init.description}
                layout="vertical"
                name="description"
                form={props?.form}
            >
                <StepWizard
                    onStepChange={({ activeStep }) => {
                        setCurrent(activeStep - 1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    instance={setInstance}
                >
                    <Step21
                        form={props?.form}
                        back={() => props?.previousStep()}
                    />
                    <Step22 form={props?.form} />
                    <Step23 form={props?.form} />
                    <Step24 form={props?.form} next={onNextStep} />
                </StepWizard>
            </Form>
        </div>
    );
}

function Step21(props) {
    const { t } = useTranslation(['translation', 'dpia']);
    const { setStep2 } = useDpiaContext();
    const onNextStep = () => {
        props.form
            .validateFields()
            .then(() => {
                setStep2(true);
            })
            .catch(() => {
                setStep2(false);
            });
        props.nextStep();
    };

    return (
        <div hidden={!props?.isActive}>
            <div className="text-xl font-semibold">
                2.1){' '}
                {t('Describe the nature of data processing as follows', {
                    ns: 'dpia',
                })}
            </div>
            {parse(t('2.1', { ns: 'dpia' }))}
            <FormObserve name="natureDescription" form={props.form} />
            <Divider />
            <StepsButton previous={props?.back} next={onNextStep} />
        </div>
    );
}

function Step22(props) {
    const { t } = useTranslation(['translation', 'dpia']);
    const { setStep2 } = useDpiaContext();
    const onNextStep = () => {
        props.form
            .validateFields()
            .then(() => {
                setStep2(true);
            })
            .catch(() => {
                setStep2(false);
            });
        props.nextStep();
    };

    const onPreviousStep = () => {
        props.previousStep();
    };
    return (
        <div hidden={!props?.isActive}>
            <div className="text-xl font-semibold">
                2.2){' '}
                {t('Specify the scope of data processing as follows', {
                    ns: 'dpia',
                })}
            </div>
            {parse(t('2.2', { ns: 'dpia' }))}
            <FormObserve name="scopeDescription" form={props.form} />
            <Divider />
            <StepsButton previous={onPreviousStep} next={onNextStep} />
        </div>
    );
}

function Step23(props) {
    const { t } = useTranslation(['translation', 'dpia']);

    const { setStep2 } = useDpiaContext();
    const onNextStep = () => {
        props.form
            .validateFields()
            .then(() => {
                setStep2(true);
            })
            .catch(() => {
                setStep2(false);
            });
        props.nextStep();
    };

    const onPreviousStep = () => {
        props.previousStep();
    };
    return (
        <div hidden={!props?.isActive}>
            <div className="text-xl font-semibold">
                2.3){' '}
                {t(
                    'Explain the context of data processing, both internal and external factors that may affect the expectations and impacts of data processing, as follows',
                    { ns: 'dpia' }
                )}
            </div>
            {parse(t('2.3', { ns: 'dpia' }))}
            <FormObserve name="contextDescription" form={props.form} />
            <Divider />
            <StepsButton previous={onPreviousStep} next={onNextStep} />
        </div>
    );
}

function Step24(props) {
    const { t } = useTranslation(['translation', 'dpia']);

    const onPreviousStep = () => {
        props.previousStep();
    };
    return (
        <div hidden={!props?.isActive}>
            <div className="text-xl font-semibold">
                2.4){' '}
                {t('Explain the purposes of data processing as follows', {
                    ns: 'dpia',
                })}
            </div>
            {parse(t('2.4', { ns: 'dpia' }))}
            <FormObserve name="purposeDescription" form={props.form} />
            <Divider />
            <StepsButton previous={onPreviousStep} next={props?.next} />
        </div>
    );
}
